import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Flex, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core'
import {
	IconEye,
	IconEyeOff,
	IconFlag,
	IconGlobe,
	IconNetwork,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import {
	getCommunityCmsCategories,
	getCommunityCmsList,
	getCommunityCmsLocationStats,
	getCommunityCmsStatistics,
	CommunityLocationStat,
} from 'src/apis/communityAPI'
import {
	deleteConversation,
	hideConversation,
} from 'src/apis/conversationAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import TableComponent from 'src/components/TableComponent'
import actionClasses from 'src/components/TableComponent/TableComponent.module.scss'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { NetworkData } from 'src/interfaces'
import { getNationalityDisplay } from 'src/pages/ActiveUsersPage/activeUserDisplay'
import { formatDateTime } from 'src/variable/constant'
import { useSearchParams } from 'react-router-dom'

interface NetworkPageListProps {
	columns: MRT_ColumnDef[]
	[key: string]: any
}

const STATUS_OPTIONS = [
	{ label: 'All Status', value: '' },
	{ label: 'Active', value: 'active' },
	{ label: 'Less active', value: 'less_active' },
	{ label: 'Inactive', value: 'inactive' },
]

const DEFAULT_STATS = {
	total_communities: 0,
	vietnam_communities: 0,
	other_country_communities: 0,
	active_communities: 0,
	less_active_communities: 0,
	inactive_communities: 0,
}

const formatCategory = (item: any) => {
	if (Array.isArray(item?.network_categories) && item.network_categories.length) {
		return item.network_categories
	}
	if (Array.isArray(item?.category_list) && item.category_list.length) {
		return item.category_list.map((category) => ({
			id: category,
			title: category,
		}))
	}
	return item?.category ? [{ id: item.category, title: item.category }] : []
}

const formatAdmins = (item: any) => {
	const admins = item?.network_admins || []
	return Array.isArray(admins) ? admins : []
}

const formatLastActive = (value: any) => {
	if (!value) return ''
	const date =
		typeof value === 'number' || /^\d+$/.test(String(value))
			? dayjs(Number(value))
			: dayjs(value)

	return date.isValid() ? date.format(formatDateTime) : ''
}

const isCommunityRowMuted = (row: any) => row?.original?.is_hide === true

function NetworkPageList({ columns }: NetworkPageListProps) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [urlSearchParams] = useSearchParams()
	const requestVersionRef = useRef(0)
	const [data, setData] = useState<NetworkData[]>([])
	const [pagination, setPagination] = useState<MRT_PaginationState>(() => ({
		pageIndex: Math.max(Number(urlSearchParams.get('page')) || 1, 1),
		pageSize: Math.max(Number(urlSearchParams.get('limit')) || 10, 1),
	}))
	const [searchKey, setSearchKey] = useState(
		() => urlSearchParams.get('search') || urlSearchParams.get('search_key') || '',
	)
	const [mainCategoryId, setMainCategoryId] = useState(
		() => urlSearchParams.get('main_category_id') || '',
	)
	const [location, setLocation] = useState(
		() => urlSearchParams.get('location') || '',
	)
	const [province, setProvince] = useState(
		() => urlSearchParams.get('province') || '',
	)
	const [country, setCountry] = useState(
		() => urlSearchParams.get('country') || '',
	)
	const [status, setStatus] = useState(
		() => urlSearchParams.get('status') || '',
	)
	const [vietnamLocationStats, setVietnamLocationStats] = useState<
		CommunityLocationStat[]
	>([])
	const [otherCountryLocationStats, setOtherCountryLocationStats] = useState<
		CommunityLocationStat[]
	>([])
	const [categoryOptions, setCategoryOptions] = useState([
		{ label: 'All Main Categories', value: '' },
	])
	const [stats, setStats] = useState(DEFAULT_STATS)
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)

	const fetchStats = useCallback(async () => {
		const res = await getCommunityCmsStatistics()
		setStats(res?.data?.results?.object || DEFAULT_STATS)
	}, [])

	const fetchOptions = useCallback(async () => {
		const [categoriesRes, vietnamRes, otherCountriesRes] = await Promise.all([
			getCommunityCmsCategories(),
			getCommunityCmsLocationStats('vietnam'),
			getCommunityCmsLocationStats('other'),
		])
		const categories = categoriesRes?.data?.results?.object || []

		setCategoryOptions([
			{ label: 'All Main Categories', value: '' },
			...categories
				.map((item: any) => ({
					label: item?.name,
					value: String(item?.id || ''),
				}))
				.filter((item: any) => item.label && item.value),
		])
		setVietnamLocationStats(vietnamRes?.data?.results?.object || [])
		setOtherCountryLocationStats(otherCountriesRes?.data?.results?.object || [])
	}, [])

	const handleGetData = useCallback(async () => {
		const requestVersion = ++requestVersionRef.current
		let arr: NetworkData[] = []
		let count = 0
		setLoading(true)
		try {
			const res = await getCommunityCmsList({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				q: searchKey || undefined,
				main_category_id: mainCategoryId || undefined,
				location: location || undefined,
				province: province || undefined,
				country: country || undefined,
				status: status || undefined,
			})
			arr = (res?.data?.results?.objects?.rows || []).map((item, index) => ({
				...item,
				no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
				created_at: dayjs(item?.created_at).format(formatDateTime),
				_category: formatCategory(item),
				_network_admins: formatAdmins(item),
				_last_active: formatLastActive(item?.last_active || item?.last_time_chat),
				amount_of_user: Number(item?.amount_of_user || 0).toLocaleString(
					'en-US',
				),
			}))
			count = res?.data?.results?.objects?.count || 0
		} catch (error) {
			if (requestVersion === requestVersionRef.current) {
				openError(error)
			}
		} finally {
			if (requestVersion === requestVersionRef.current) {
				setData(arr)
				setTotal(count)
				setTimeout(() => {
					if (requestVersion === requestVersionRef.current) {
						setLoading(false)
					}
				}, 300)
			}
		}
	}, [country, location, mainCategoryId, openError, pagination, province, searchKey, status])

	useEffect(() => () => {
		requestVersionRef.current += 1
	}, [])

	const refreshPage = useCallback(async () => {
		await Promise.all([handleGetData(), fetchStats()])
	}, [fetchStats, handleGetData])

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const resetPage = useCallback(() => {
		setPagination((pre) => ({
			...pre,
			pageIndex: 1,
		}))
	}, [])

	const handleSearchQueryInit = useCallback((queryValue: string) => {
		setSearchKey(queryValue || '')
	}, [])

	const handleFilterQueryInit = useCallback((query: Record<string, string>) => {
		setMainCategoryId(query.main_category_id || '')
		setLocation(query.location || '')
		setProvince(query.province || '')
		setCountry(query.country || '')
		setStatus(query.status || '')
	}, [])

	const applyLocationFilter = useCallback((nextLocation = '', nextProvince = '', nextCountry = '') => {
		setLocation(nextLocation)
		setProvince(nextProvince)
		setCountry(nextCountry)
		resetPage()
	}, [resetPage])

	const handleDeleteNetwork = useCallback(
		async (id) => {
			openLoadingContext()
			try {
				const res = await deleteConversation(id)
				if (res?.data?.code === 200) {
					await refreshPage()
					openModal({
						type: 'success',
						label: 'You have successfully deleted this network !',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, openError, openLoadingContext, openModal, refreshPage],
	)

	const handleDelete = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to delete this network ?</div>,
				handleConfirm: () => {
					handleDeleteNetwork(row?.original?.id)
				},
			})
		},
		[handleDeleteNetwork, openModal],
	)

	const handleHideNetwork = useCallback(
		async (row: any) => {
			const isHide = row?.original?.is_hide === true
			openLoadingContext()
			try {
				const res = await hideConversation(row?.original?.id, !isHide)
				if (res?.data?.code === 200) {
					await refreshPage()
					openModal({
						type: 'success',
						label: isHide
							? 'Community has been shown successfully!'
							: 'Community has been hidden successfully!',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, openError, openLoadingContext, openModal, refreshPage],
	)

	const handleConfirmHide = useCallback(
		(row: any) => {
			const isHide = row?.original?.is_hide === true
			openModal({
				type: 'confirm',
				label: (
					<div>
						Do you want to {isHide ? 'show' : 'hide'} this community?
					</div>
				),
				handleConfirm: () => {
					handleHideNetwork(row)
				},
			})
		},
		[handleHideNetwork, openModal],
	)

	useEffect(() => {
		fetchOptions().catch(openError)
	}, [fetchOptions, openError])

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.pageIndex, pagination.pageSize])

	useEffect(() => {
		fetchStats().catch(openError)
	}, [fetchStats, openError])

	useEffect(() => {
		const timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				handleGetData()
			} else {
				resetPage()
			}
		}, 400)
		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchKey, mainCategoryId, location, province, country, status])

	const countryOptions = useMemo(() => {
		const vietnamCount = Number(stats.vietnam_communities || 0)

		return [
			{ label: 'All Countries', value: '' },
			{ label: `Vietnam (${vietnamCount})`, value: 'vietnam' },
			{
				label: `Other Countries (${Number(stats.other_country_communities || 0)})`,
				value: 'other',
			},
			...otherCountryLocationStats.map((item) => {
				const countryName = getNationalityDisplay('', item.location_label).name
				return {
					label: `${countryName === '-' ? item.location_label : countryName} (${Number(item.community_count || 0)})`,
					value: `country:${item.location_key}`,
				}
			}),
		]
	}, [
		otherCountryLocationStats,
		stats.other_country_communities,
		stats.vietnam_communities,
	])

	const provinceOptions = useMemo(
		() => [
			{ label: 'All Provinces / Cities', value: '' },
			...vietnamLocationStats.map((item) => ({
				label: `${item.location_label} (${Number(item.community_count || 0)})`,
				value: item.location_label,
			})),
		],
		[vietnamLocationStats],
	)

	const countryFilterValue = location === 'vietnam'
		? 'vietnam'
		: location === 'other'
			? 'other'
		: country
			? `country:${country.toLowerCase()}`
			: ''

	const handleCountryFilterChange = useCallback(
		(value: string | null) => {
			if (!value) {
				applyLocationFilter()
				return
			}

			if (value === 'vietnam') {
				applyLocationFilter('vietnam')
				return
			}

			if (value === 'other') {
				applyLocationFilter('other')
				return
			}

			applyLocationFilter('', '', value.replace(/^country:/, ''))
		},
		[applyLocationFilter],
	)

	const statItems = useMemo(
		() => [
			{
				label: 'Total Communities',
				value: stats.total_communities,
				icon: IconGlobe,
				color: '#168d4f',
				background: '#edf8f0',
			},
			{
				label: 'Communities in Vietnam',
				value: stats.vietnam_communities,
				icon: IconFlag,
				color: '#e55a0f',
				background: '#fff3e8',
			},
			{
				label: 'Other Countries',
				value: stats.other_country_communities,
				icon: IconNetwork,
				color: '#5b21b6',
				background: '#f1eafe',
			},
		],
		[stats],
	)

	const renderCustomActions = (row: any) => {
		const isHide = row?.original?.is_hide === true
		return (
			<Tooltip
				withinPortal
				withArrow
				label={isHide ? 'Show network' : 'Hide network'}
			>
				<button
					type="button"
					onClick={() => handleConfirmHide(row)}
					className={`${actionClasses.actionButton} ${
						isHide ? actionClasses.mutedAction : actionClasses.primaryAction
					}`}
				>
					{isHide ? <IconEyeOff size={16} /> : <IconEye size={16} />}
				</button>
			</Tooltip>
		)
	}

	return (
		<Stack sx={{ width: '100%' }} spacing={18}>
			<Stack spacing={4}>
				<Text size={24} fw={800} color="#111827">
					Communities
				</Text>
				<Text size="sm" color="dimmed">
					Manage all communities in the system
				</Text>
			</Stack>

			<SimpleGrid
				cols={3}
				breakpoints={[
					{ maxWidth: 'md', cols: 2 },
					{ maxWidth: 'sm', cols: 1 },
				]}
			>
				{statItems.map((item) => {
					const Icon = item.icon
					const filterLocation =
						item.label === 'Communities in Vietnam'
							? 'vietnam'
							: item.label === 'Other Countries'
								? 'other'
								: ''
					return (
						<Card
							key={item.label}
							withBorder
							radius="md"
							p="lg"
							onClick={() => applyLocationFilter(filterLocation)}
							sx={{ cursor: 'pointer' }}
						>
							<Flex align="center" gap={18}>
								<Flex
									align="center"
									justify="center"
									sx={{
										width: 54,
										height: 54,
										borderRadius: 10,
										background: item.background,
										color: item.color,
									}}
								>
									<Icon size={28} stroke={2} />
								</Flex>
								<Stack spacing={2}>
									<Text size={28} fw={800} color={item.color}>
										{Number(item.value || 0).toLocaleString('en-US')}
									</Text>
									<Text size="sm" color="#111827">
										{item.label}
									</Text>
								</Stack>
							</Flex>
						</Card>
					)
				})}
			</SimpleGrid>

			<Flex gap={14} sx={{ flexWrap: 'wrap' }}>
				<CustomClearInput
					value={searchKey}
					onChange={(value) => setSearchKey(value)}
					placeholder="Search by title, admin or location..."
					sx={{ flex: '1 1 280px', minWidth: 280 }}
				/>
				<CustomSelect
					value={mainCategoryId}
					data={categoryOptions}
					onChange={(value) => {
						setMainCategoryId(value || '')
						resetPage()
					}}
					sx={{ flex: '1 1 220px', minWidth: 220 }}
				/>
				<CustomSelect
					value={countryFilterValue}
					data={countryOptions}
					onChange={handleCountryFilterChange}
					placeholder="Country"
					searchable
					clearable
					nothingFound="No countries found"
					sx={{ flex: '1 1 220px', minWidth: 220 }}
				/>
				{location === 'vietnam' && (
					<CustomSelect
						value={province}
						data={provinceOptions}
						onChange={(value) => {
							setProvince(value || '')
							resetPage()
						}}
						placeholder="Province / City"
						searchable
						clearable
						nothingFound="No provinces or cities found"
						sx={{ flex: '1 1 220px', minWidth: 220 }}
					/>
				)}
				<CustomSelect
					value={status}
					data={STATUS_OPTIONS}
					onChange={(value) => {
						setStatus(value || '')
						resetPage()
					}}
					sx={{ flex: '1 1 220px', minWidth: 220 }}
				/>
			</Flex>

			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				searchQueryValue={searchKey || undefined}
				onSearchQueryInit={handleSearchQueryInit}
				filterQueryValues={{
					category_id: undefined,
					main_category_id: mainCategoryId || undefined,
					location: location || undefined,
					province: province || undefined,
					country: country || undefined,
					status: status || undefined,
				}}
				onFilterQueryInit={handleFilterQueryInit}
				handleDelete={handleDelete}
				_renderCustomActions={renderCustomActions}
				actionColumnSize={92}
				isRowMuted={isCommunityRowMuted}
			/>

			<Card withBorder radius="md" p="sm" sx={{ maxWidth: 520 }}>
				<Flex gap={28} wrap="wrap">
					{[
						{ label: 'Active', detail: 'Last chat in 1 week', color: '#16a34a' },
						{
							label: 'Less active',
							detail: 'Last chat 7 - 15 days ago',
							color: '#f59e0b',
						},
						{ label: 'Inactive', detail: 'Last chat > 15 days', color: '#ef4444' },
					].map((item) => (
						<Flex key={item.label} align="flex-start" gap={8}>
							<span
								style={{
									width: 8,
									height: 8,
									borderRadius: 999,
									background: item.color,
									marginTop: 7,
								}}
							/>
							<Stack spacing={0}>
								<Text size="xs" fw={700} color={item.color}>
									{item.label}
								</Text>
								<Text size="xs" color="dimmed">
									{item.detail}
								</Text>
							</Stack>
						</Flex>
					))}
				</Flex>
			</Card>
		</Stack>
	)
}

export default memo(NetworkPageList)
